import React, { useEffect } from "react";
import { TextField, Switch, Box, Grid, Backdrop, Button } from "@material-ui/core";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import { makeStyles, Theme } from "@material-ui/core/styles";
import EditIcon from "@material-ui/icons/Edit";
import { useFormik } from "formik";
import { object, string } from "yup";
import {
  addNewShed,
  updateShad,
  getAllShads,
} from "src/app-redux/configuration/actions/configurationActions";
import { loader, selectAllShadList } from "src/app-redux/configuration/configurationSlice";
import { useAppDispatch, useAppSelector } from "src/app-redux/hooks";
import { IAddShad, IAddWarehouse } from "src/common/typings/configuration";
import NewCommonButton from "src/components/CommonButton/NewCommonButton";
import CommonButton from "../../../../../../components/CommonButton/CommonButton";
import { columns } from "./columns";
import { Prompt } from "react-router-dom";
import { Check, Close } from "@material-ui/icons";
import { Tooltip } from "@mui/material";
import CommonMaterialGrid from "src/components/CommonXGrid/CommonMaterialGrid";
import CommonLoader from "src/components/Loader/CommonLoader";

const useStyles = makeStyles((theme: Theme) => ({
  formRow: {
    marginBottom: "0px",
  },
  backdrop: {
    zIndex: theme.zIndex.modal + 1,
    color: "#fff",
  },
  tabCardContent: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    overflow: "auto",
  },

  UserMainPanel: {
    width: "100%",
    float: "left",
    padding: "0px 20px",
    paddingRight: 0,
  },
  mainConainer: { marginTop: "0px" },
}));

export interface IForm extends IAddWarehouse {}

interface Props {}

export function InputFields({}: Props): React.ReactElement {
  const classes = useStyles();
  const dispatch = useAppDispatch();

  const [displayColumns, setDisplayColumns] = React.useState<any>(columns);
  const [isEditMode, setisEditMode] = React.useState<boolean>(false);

  const loading = useAppSelector(loader);
  const allShadRows = useAppSelector(selectAllShadList);

  // Form Submission
  const validationSchema = object({
    name: string().required("This field is required").max(50, "Maximum 50 characters are allowed."),
  });

  const formik = useFormik<IAddShad>({
    enableReinitialize: true,
    initialValues: {
      id: "",
      name: "",
      isActive: false,
    },
    validationSchema: validationSchema,
    onSubmit: async (values, actions) => {
      let res;
      if (isEditMode) res = await dispatch(updateShad(values));
      else res = await dispatch(addNewShed(values));
      if (res?.payload) {
        const getActiveSheds = false;
        dispatch(getAllShads(getActiveSheds));
        actions.setSubmitting(false);
        actions.resetForm();
        setisEditMode(false);
        setIsDirty(false);
      }
    },
  });
  // End Form Submission
  const handleUpdateClick = (params: any) => {
    setisEditMode(true);
    formik.setFieldValue("id", params.id);
    formik.setFieldValue("name", params.name);
    formik.setFieldValue("isActive", params.isActive);
  };
  useEffect(() => {
    const getActiveSheds = false;
    dispatch(getAllShads(getActiveSheds));
  }, []);

  /* Transform Column */
  useEffect(() => {
    if (allShadRows.length) {
      const transformedColumns: any = columns.map((column: any) =>
        column.accessorKey === "actions"
          ? {
              ...column,
              Cell: function customCell({ row }: any) {
                return (
                  <>
                    <NewCommonButton
                      onClick={() => {
                        setIsDirty(true);
                        handleUpdateClick(row.original);
                      }}
                      icon={<EditIcon />}
                      isEdit={true}
                    >
                      Edit
                    </NewCommonButton>
                  </>
                );
              },
            }
          : column.accessorKey === "isActive"
          ? {
              ...column,
              Cell: function customCell({ row }: any) {
                return (
                  <React.Fragment>
                    <Box>
                      <Tooltip title={row.original?.isActive ? "Yes" : "No"}>
                        <Button style={{ minWidth: "30px", padding: "4px", color: "gray" }}>
                          {row.original?.isActive ? <Check /> : <Close />}
                        </Button>
                      </Tooltip>
                    </Box>
                  </React.Fragment>
                );
              },
            }
          : column
      );

      setDisplayColumns(transformedColumns);
    }
  }, [allShadRows]);

  const [isDirty, setIsDirty] = React.useState<boolean>(false);
  useEffect(() => {
    window.onbeforeunload = function (e: Event) {
      if (isDirty) {
        e.preventDefault();
        return "Changes you made may not be saved.";
      }
    };
  }, [isDirty]);
  function handleChange(e: any) {
    formik.handleChange(e);
    e.target.value || JSON.stringify(formik.values) !== JSON.stringify(formik.initialValues)
      ? setIsDirty(true)
      : setIsDirty(false);
  }

  return (
    <>
      {/* If prompt, when navigating to another page inside the app */}
      <Prompt when={isDirty} message="Changes you made may not be saved." />
      <form onSubmit={formik.handleSubmit}>
        <Backdrop className={classes.backdrop} open={loading}>
          <CommonLoader />
        </Backdrop>
        <Grid container direction="row" alignItems="flex-start">
          {/* First Section */}
          <>
            <Grid className={classes.UserMainPanel}>
              <Grid
                // Container
                container
                className={classes.mainConainer}
                direction="row"
                alignItems="flex-start"
                spacing={2}
              >
                <Grid container item xs={12} spacing={2}>
                  <Grid container item xs={12} spacing={2} style={{ paddingRight: "0px" }}>
                    <Grid
                      item
                      xs={12}
                      md={5}
                      lg={4}
                      style={{ paddingLeft: "0px", paddingRight: "0px" }}
                    >
                      <Box sx={{ marginRight: { xs: 0, lg: "5px" } }}>
                        <TextField
                          className="customTextField"
                          type="string"
                          required
                          size="small"
                          fullWidth
                          variant="outlined"
                          InputLabelProps={{
                            shrink: true,
                          }}
                          label="Shed Type"
                          placeholder="Enter Shed Type"
                          name="name"
                          disabled={false}
                          value={formik.values.name}
                          onChange={handleChange}
                          error={formik.touched.name && Boolean(formik.errors.name)}
                          helperText={formik.touched.name && formik.errors.name}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={6} md={2} style={{ paddingLeft: "0px" }}>
                      <Box>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={Boolean(formik.values.isActive)}
                              onChange={formik.handleChange}
                              name="isActive"
                              color="primary"
                            />
                          }
                          label="Active"
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={6} md={2} style={{ paddingRight: 0 }}>
                      <Box
                        mb={1}
                        mt={1}
                        className={"headerButtonsConfig"}
                        style={{ float: "right" }}
                      >
                        <CommonButton
                          isGreen
                          cssClass="customSubmitBtn"
                          onClick={() => {
                            formik.submitForm();
                          }}
                        >
                          {isEditMode ? "Update" : "Add"}
                        </CommonButton>
                      </Box>
                    </Grid>
                  </Grid>
                  <Grid md={12}>
                    <CommonMaterialGrid
                      defaultPerPageRecord={10}
                      height={400}
                      rows={allShadRows || []}
                      columns={displayColumns}
                    />
                  </Grid>
                </Grid>
                {/* End Inner Container */}
              </Grid>
              {/* End Outer Container */}
            </Grid>
          </>
        </Grid>
      </form>
    </>
  );
}
